import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  Lock, Unlock, TrendingUp, Calendar, DollarSign, Target, 
  BarChart3, Activity, CheckCircle, Clock, AlertCircle, Lightbulb, Info, X
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvestmentPage = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [investmentData, setInvestmentData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTrimestre, setSelectedTrimestre] = useState(null);
  const [trimestreDetails, setTrimestreDetails] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/investment/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      const data = await response.json();

      if (data.success) {
        setAuthenticated(true);
        fetchInvestmentData();
      } else {
        setError(data.message || 'Contraseña incorrecta');
        setPassword('');
      }
    } catch (err) {
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvestmentData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/investment/data`);
      const data = await response.json();
      setInvestmentData(data);
    } catch (err) {
      console.error('Error fetching investment data:', err);
    }
  };

  const fetchTrimestreDetails = async (trimestre) => {
    try {
      const response = await fetch(`${API_URL}/api/investment/trimestre/${trimestre}`);
      const data = await response.json();
      setTrimestreDetails(data);
      setSelectedTrimestre(trimestre);
      setModalOpen(true);
    } catch (err) {
      console.error('Error fetching trimestre details:', err);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  };

  const quarterColors = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#14b8a6', '#6366f1'];

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="w-full max-w-md shadow-2xl border-0">
            <CardHeader className="text-center space-y-4 pb-8">
              <div className="mx-auto w-20 h-20 bg-gradient-to-br from-green-600 to-green-700 rounded-full flex items-center justify-center shadow-lg">
                <Lock className="w-10 h-10 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold text-gray-900">Sección de Inversión</CardTitle>
                <CardDescription className="text-base mt-2">
                  Ingresa la contraseña para acceder al plan de inversión tecnológica
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Input
                    type="password"
                    placeholder="Contraseña de acceso"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 text-center text-lg tracking-widest"
                    disabled={loading}
                  />
                  {error && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex items-center gap-2 text-red-600 text-sm"
                    >
                      <AlertCircle className="w-4 h-4" />
                      <span>{error}</span>
                    </motion.div>
                  )}
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold text-base"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <Clock className="w-4 h-4 animate-spin" />
                      Verificando...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Unlock className="w-4 h-4" />
                      Acceder
                    </span>
                  )}
                </Button>
              </form>
              <div className="mt-6 pt-6 border-t">
                <Link to="/">
                  <Button variant="outline" className="w-full">
                    Volver al inicio
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  if (!investmentData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Clock className="w-12 h-12 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Cargando datos de inversión...</p>
        </div>
      </div>
    );
  }

  const { quarterly, monthly, pert, summary } = investmentData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-green-50">
      {/* Modal for Trimestre Details */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {trimestreDetails && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-green-700 flex items-center gap-2">
                  <Target className="w-6 h-6" />
                  {trimestreDetails.titulo}
                </DialogTitle>
                <DialogDescription className="text-base">
                  {trimestreDetails.meses} | Inversión: {trimestreDetails.inversion}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Objetivo */}
                <div>
                  <h3 className="font-semibold text-lg text-gray-800 mb-2 flex items-center gap-2">
                    <Target className="w-5 h-5 text-green-600" />
                    Objetivo Principal
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{trimestreDetails.objetivo}</p>
                </div>

                {/* Entregables */}
                <div>
                  <h3 className="font-semibold text-lg text-gray-800 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                    Entregables ({trimestreDetails.entregables.length})
                  </h3>
                  <ul className="space-y-2">
                    {trimestreDetails.entregables.map((item, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="flex items-start gap-3 text-gray-700"
                      >
                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>

                {/* Hitos Clave */}
                <div>
                  <h3 className="font-semibold text-lg text-gray-800 mb-3 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-purple-600" />
                    Hitos Clave
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {trimestreDetails.hitos_clave.map((hito, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-start gap-2"
                      >
                        <Badge className="bg-purple-600 mt-0.5 flex-shrink-0">{index + 1}</Badge>
                        <span className="text-sm text-gray-700">{hito}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Tecnologías */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-lg text-gray-800 mb-2 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-orange-600" />
                    Tecnologías Principales
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{trimestreDetails.tecnologias}</p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="bg-gradient-to-r from-green-700 via-green-600 to-green-700 text-white shadow-xl">
        <div className="container mx-auto px-4 py-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between"
          >
            <div>
              <div className="flex items-center gap-3 mb-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_5732c38f-46bd-4a56-9931-c3cf86a70f4f/artifacts/nfkada7y_logo.jpg" 
                  alt="Banco Agrícola" 
                  className="h-14 w-auto bg-white/10 rounded-lg p-1" 
                />
                <Badge className="bg-white/20 text-white border-white/30">Código 0166</Badge>
              </div>
              <h1 className="text-4xl font-bold mb-2">Plan de Inversión Tecnológica</h1>
              <p className="text-green-100 text-lg">Proyecto de Modernización Digital 2025-2027</p>
            </div>
            <Link to="/">
              <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                ← Volver al Inicio
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <Card className="border-l-4 border-l-green-600 shadow-lg hover:shadow-xl transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-600">Inversión Total</CardTitle>
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-700">{formatCurrency(summary.total_investment)}</p>
              <p className="text-sm text-gray-500 mt-1">USD en {summary.num_quarters} trimestres</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-600 shadow-lg hover:shadow-xl transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-600">Duración</CardTitle>
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-blue-700">{summary.duration_months} meses</p>
              <p className="text-sm text-gray-500 mt-1">{summary.duration_weeks} semanas planificadas</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-600 shadow-lg hover:shadow-xl transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-600">Promedio Trimestral</CardTitle>
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-purple-700">{formatCurrency(summary.avg_quarterly)}</p>
              <p className="text-sm text-gray-500 mt-1">Por trimestre</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-600 shadow-lg hover:shadow-xl transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-600">Actividades</CardTitle>
                <Target className="w-5 h-5 text-orange-600" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-orange-700">{pert.length}</p>
              <p className="text-sm text-gray-500 mt-1">Actividades principales</p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Tabs for different views */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid h-auto p-1">
              <TabsTrigger value="overview" className="gap-2">
                <BarChart3 className="w-4 h-4" />
                Visión General
              </TabsTrigger>
              <TabsTrigger value="timeline" className="gap-2">
                <Activity className="w-4 h-4" />
                Cronograma PERT
              </TabsTrigger>
              <TabsTrigger value="monthly" className="gap-2">
                <Calendar className="w-4 h-4" />
                Actividades Mensuales
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-green-600" />
                    Inversión por Trimestre
                  </CardTitle>
                  <CardDescription>Distribución de recursos en el proyecto de modernización</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={quarterly}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="trimestre" stroke="#6b7280" />
                      <YAxis 
                        stroke="#6b7280"
                        tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                      />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Legend />
                      <Bar dataKey="erogacion" name="Inversión" radius={[8, 8, 0, 0]}>
                        {quarterly.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={quarterColors[index % quarterColors.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {quarterly.map((quarter, index) => (
                  <motion.div
                    key={quarter.trimestre}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * index }}
                  >
                    <Card 
                      className="h-full shadow-md hover:shadow-xl transition-all border-l-4 cursor-pointer group" 
                      style={{ borderLeftColor: quarterColors[index] }}
                      onClick={() => fetchTrimestreDetails(quarter.trimestre)}
                    >
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-xl group-hover:text-green-600 transition-colors">{quarter.trimestre}</CardTitle>
                          <div className="flex items-center gap-2">
                            <Badge style={{ backgroundColor: quarterColors[index] }} className="text-white">
                              Meses {quarter.meses}
                            </Badge>
                            <Info className="w-5 h-5 text-gray-400 group-hover:text-green-600 transition-colors" />
                          </div>
                        </div>
                        <CardDescription className="text-2xl font-bold mt-2" style={{ color: quarterColors[index] }}>
                          {formatCurrency(quarter.erogacion)}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-start gap-2">
                          <Lightbulb className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-600 leading-relaxed">{quarter.descripcion}</p>
                        </div>
                        <div className="mt-4 text-sm text-green-600 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                          <span>Ver detalles completos</span>
                          <Info className="w-4 h-4" />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </TabsContent>

            {/* PERT Timeline Tab */}
            <TabsContent value="timeline" className="space-y-6">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-blue-600" />
                    Diagrama PERT / Gantt Completo
                  </CardTitle>
                  <CardDescription>Cronograma visual de todas las {pert.length} actividades del proyecto (96 semanas / 24 meses)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1 overflow-x-auto">
                    {/* Timeline header - showing weeks grouped by trimester */}
                    <div className="flex items-center gap-2 mb-4 min-w-[2400px]">
                      <div className="w-72 flex-shrink-0 font-semibold text-xs text-gray-600 sticky left-0 bg-white z-10 pr-2">Actividad</div>
                      <div className="flex-1 relative h-10 bg-gray-100 rounded">
                        <div className="absolute inset-0 flex">
                          {[...Array(8)].map((_, i) => (
                            <div key={i} className="flex-1 border-r-2 border-gray-400 flex flex-col items-center justify-center text-xs font-semibold text-gray-700">
                              <span>T{i+1}</span>
                              <span className="text-xs text-gray-500">S{i*12+1}-{(i+1)*12}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Timeline bars */}
                    {pert.map((activity, index) => {
                      // Activities are in weeks (1-96), but need to map correctly to full timeline
                      // The timeline spans 96 weeks total
                      const start = ((activity.inicio - 1) / 96) * 100;
                      const duration = ((activity.fin - activity.inicio + 1) / 96) * 100;
                      
                      const colors = [
                        'bg-gradient-to-r from-green-500 to-green-600',
                        'bg-gradient-to-r from-blue-500 to-blue-600',
                        'bg-gradient-to-r from-purple-500 to-purple-600',
                        'bg-gradient-to-r from-orange-500 to-orange-600',
                        'bg-gradient-to-r from-pink-500 to-pink-600',
                        'bg-gradient-to-r from-cyan-500 to-cyan-600',
                        'bg-gradient-to-r from-teal-500 to-teal-600',
                        'bg-gradient-to-r from-indigo-500 to-indigo-600'
                      ];
                      const color = colors[index % colors.length];

                      return (
                        <motion.div
                          key={activity.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.01 * index }}
                          className="flex items-center gap-2 py-2 hover:bg-gray-50 rounded min-w-[2400px]"
                        >
                          <div className="w-72 flex-shrink-0 text-xs font-medium text-gray-700 sticky left-0 bg-white z-10 pr-2" title={activity.actividad}>
                            <span className="block truncate">{activity.actividad}</span>
                            <span className="block text-xs text-gray-500 truncate">{activity.categoria}</span>
                          </div>
                          <div className="flex-1 relative h-8">
                            <div
                              className={`absolute h-6 rounded-lg shadow-md ${color} flex items-center justify-center text-white text-xs font-semibold hover:shadow-lg transition-shadow cursor-pointer`}
                              style={{
                                left: `${start}%`,
                                width: `${Math.max(duration, 2)}%`,
                                minWidth: '30px'
                              }}
                              title={`${activity.actividad}\nSemanas ${activity.inicio}-${activity.fin}\nDuración: ${activity.fin - activity.inicio + 1} semanas`}
                            >
                              <span className="px-1 truncate text-xs">S{activity.inicio}-{activity.fin}</span>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Monthly Activities Tab */}
            <TabsContent value="monthly" className="space-y-6">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-purple-600" />
                    Cronograma Mensual de Actividades
                  </CardTitle>
                  <CardDescription>Plan detallado mes a mes del proyecto</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {monthly.map((item, index) => (
                      <motion.div
                        key={item.mes}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.02 * index }}
                        className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-green-600 to-green-700 rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                            {item.mes}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 font-medium mb-1">MES {item.mes}</p>
                            <p className="text-sm text-gray-800 font-medium leading-relaxed">{item.actividad}</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
};

export default InvestmentPage;